// Sight Sprint: a sentence flashes, then a quick question. Reading fluency without stress:
// "Show again" is always there.

import { h, clear } from '@kit/dom';
import type { GameModule, GameContext } from '@shell/types';
import { makeSprintItem, exposureMs, type SprintQ } from '@content/words/sprint';

const ITEMS = 10;

function mount(host: HTMLElement, ctx: GameContext) {
  let n = 0;
  let level = ctx.currentLevel();
  const used = new Set<string>();
  let it: SprintQ = makeSprintItem(level, ctx.rng, used);
  let attempts = 0, correct = 0, minLevel = level;
  let hinted = false, locked = false, showing = false;
  let timer = 0, barTimer = 0;
  const t0 = performance.now();
  let tProblem = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Sprint ', h('strong', null, '1'), ` of ${ITEMS}`), h('span.muted.small', null, ''));
  const card = h('div.card.center', { style: { minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: '700', lineHeight: '1.4', padding: '16px' } });
  const bar = h('div', { style: { height: '6px', background: 'var(--line)', borderRadius: '3px', overflow: 'hidden' } }, h('div', { style: { height: '100%', width: '100%', background: 'var(--words)', transition: 'width .1s linear' } }));
  const question = h('p.center', { style: { fontWeight: '700', minHeight: '28px' } });
  const choices = h('div.stack', { style: { gap: '8px' } });
  const actions = h('div.grid2', null,
    h('button.btn', { onclick: showAgain }, 'Show again'),
    h('button.btn.primary', { onclick: start }, 'Ready'),
  );
  host.append(head, card, bar, question, choices, actions);
  const readyBtn = actions.lastElementChild as HTMLButtonElement;
  const againBtn = actions.firstElementChild as HTMLButtonElement;

  function setup() {
    level = ctx.currentLevel(); minLevel = Math.min(minLevel, level);
    it = makeSprintItem(level, ctx.rng, used);
    hinted = false; locked = false; showing = false;
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    (head.lastElementChild as HTMLElement).textContent = `${(exposureMs(it.text, level) / 1000).toFixed(1)} s`;
    card.textContent = 'Tap Ready, then read fast.';
    card.style.color = 'var(--text-2)';
    question.textContent = '';
    clear(choices);
    readyBtn.style.display = ''; againBtn.disabled = true;
    (bar.firstElementChild as HTMLElement).style.width = '100%';
  }

  function flash(ms: number, after: () => void) {
    clearTimeout(timer); clearInterval(barTimer);
    showing = true;
    card.textContent = it.text; card.style.color = '';
    const fill = bar.firstElementChild as HTMLElement;
    const start = performance.now();
    barTimer = window.setInterval(() => { const f = Math.max(0, 1 - (performance.now() - start) / ms); fill.style.width = `${f * 100}%`; }, 80);
    timer = window.setTimeout(() => { clearInterval(barTimer); fill.style.width = '0%'; showing = false; card.textContent = '· · ·'; card.style.color = 'var(--text-2)'; after(); }, ms);
  }

  function start() {
    readyBtn.style.display = 'none';
    tProblem = performance.now();
    flash(exposureMs(it.text, level), ask);
  }

  function ask() {
    againBtn.disabled = false;
    question.textContent = it.q;
    clear(choices);
    for (const c of it.choices) choices.append(h('button.choice', { style: { fontSize: '18px', minHeight: '52px', padding: '6px 10px' }, onclick: () => answer(c) }, c));
  }

  function showAgain() {
    if (locked || showing) return;
    hinted = true;
    ctx.sound('pop');
    flash(Math.max(1500, exposureMs(it.text, level)), () => { ctx.say('There it was again. Now answer.'); });
  }

  function answer(v: string) {
    if (locked) return;
    locked = true;
    const ok = v === it.answer;
    const ms = performance.now() - tProblem;
    attempts++; if (ok) correct++;
    ctx.report({ skill: it.q.startsWith('Which word') ? 'vocabulary' : 'comprehension', level, correct: ok, hintUsed: hinted, ms, problemId: `sprint:${it.text.slice(0, 20)}` });
    card.textContent = it.text; card.style.color = '';
    if (ok) { ctx.sound('ding'); ctx.say('Yes!'); }
    else { ctx.sound('buzz'); ctx.say(`It was "${it.answer}". Here is the sentence again.`); }
    for (const b of Array.from(choices.children) as HTMLButtonElement[]) { b.disabled = true; if (b.textContent === it.answer) b.classList.add('good'); else if (b.textContent === v && !ok) b.classList.add('bad'); }
    n++;
    if (n >= ITEMS) {
      if (attempts === correct && minLevel >= 7) ctx.charm('charm.sprint');
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), 1400);
      return;
    }
    setTimeout(setup, ok ? 1200 : 2200);
  }

  setup();
  return { destroy() { clearTimeout(timer); clearInterval(barTimer); clear(host); } };
}

const mod: GameModule = {
  id: 'words.sight-sprint',
  title: 'Sight Sprint',
  blurb: 'A sentence flashes by. Catch it, then answer a quick question.',
  category: 'words',
  skills: ['vocabulary', 'comprehension'],
  levelRange: [1, 10],
  unlock: { stars: 12 },
  mount,
};
export default mod;
